import { useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import {
  OfflineSession,
  saveOfflineSession,
  getOfflineSession,
  updateOfflineSessionTimestamp,
  clearOfflineSession,
  removeCachedData,
} from '@/lib/indexedDB';

export type AppRole = 'admin' | 'viewer';

interface AuthState {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isApproved: boolean;
  isLoading: boolean;
  isOfflineMode: boolean;
  offlineSession: OfflineSession | null;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    isAdmin: false,
    isApproved: false,
    isLoading: true,
    isOfflineMode: false,
    offlineSession: null,
  });

  const lastUserIdRef = useRef<string | null>(null);

  const checkUserRole = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking user role:', error);
        return false;
      }

      return data?.role === 'admin';
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  }, []);

  const checkUserApproval = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('approved')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking user approval:', error);
        return false;
      }

      return data?.approved ?? false;
    } catch (error) {
      console.error('Error checking user approval:', error);
      return false;
    }
  }, []);

  const saveSessionForOffline = useCallback(async (
    userId: string,
    email: string,
    isAdmin: boolean,
    isApproved: boolean,
    fullName?: string
  ) => {
    try {
      await saveOfflineSession({
        userId,
        email,
        fullName,
        isAdmin,
        isApproved,
      });
    } catch (error) {
      console.error('Error saving offline session:', error);
    }
  }, []);

  const tryOfflineLogin = useCallback(async (): Promise<OfflineSession | null> => {
    try {
      const offlineSession = await getOfflineSession();
      
      if (offlineSession && offlineSession.isApproved) {
        console.log('📴 Using offline session for user:', offlineSession.email);
        return offlineSession;
      }
      
      return null;
    } catch (error) {
      console.error('Error trying offline login:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAccessFlags = async (userId: string) => {
      const [isAdmin, isApproved] = await Promise.all([
        checkUserRole(userId),
        checkUserApproval(userId),
      ]);
      return {
        isAdmin,
        isApproved: isAdmin || isApproved, // Admins are always approved
      };
    };

    // Listener for ONGOING auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      const nextUserId = session?.user?.id ?? null;
      const userChanged = nextUserId !== lastUserIdRef.current;
      lastUserIdRef.current = nextUserId;

      // Update session/user immediately
      setAuthState((prev) => {
        // Logout
        if (!session?.user) {
          return {
            session: null,
            user: null,
            isAdmin: false,
            isApproved: false,
            isLoading: false,
            isOfflineMode: false,
            offlineSession: null,
          };
        }

        // If user changed (fresh login), block UI until role/approval is loaded
        if (userChanged) {
          return {
            ...prev,
            session,
            user: session.user,
            isAdmin: false,
            isApproved: false,
            isLoading: true,
            isOfflineMode: false,
          };
        }

        // Same user (e.g. token refresh) → keep flags, avoid flicker
        return {
          ...prev,
          session,
          user: session.user,
        };
      });

      // Only (re)load access flags when the user actually changed
      if (session?.user && userChanged) {
        setTimeout(async () => {
          try {
            const { isAdmin, isApproved } = await loadAccessFlags(session.user.id);
            
            // Save session for offline use when online
            if (isApproved && navigator.onLine) {
              await saveSessionForOffline(
                session.user.id,
                session.user.email || '',
                isAdmin,
                isApproved,
                session.user.user_metadata?.full_name
              );
              await updateOfflineSessionTimestamp();
            }
            
            if (!isMounted) return;
            setAuthState((prev) => ({
              ...prev,
              isAdmin,
              isApproved,
              isLoading: false,
            }));
          } catch (error) {
            console.error('Error checking access flags:', error);
            if (!isMounted) return;
            setAuthState((prev) => ({ ...prev, isLoading: false }));
          }
        }, 0);
      }
    });

    // INITIAL load (controls isLoading)
    const initializeAuth = async () => {
      try {
        // Check if we're offline first
        if (!navigator.onLine) {
          const offlineSession = await tryOfflineLogin();
          if (offlineSession) {
            if (!isMounted) return;
            lastUserIdRef.current = offlineSession.userId;
            setAuthState({
              session: null,
              user: null,
              isAdmin: offlineSession.isAdmin,
              isApproved: offlineSession.isApproved,
              isLoading: false,
              isOfflineMode: true,
              offlineSession,
            });
            return;
          }
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (!session?.user) {
          // If online failed and we're offline, try offline login
          if (!navigator.onLine) {
            const offlineSession = await tryOfflineLogin();
            if (offlineSession) {
              lastUserIdRef.current = offlineSession.userId;
              setAuthState({
                session: null,
                user: null,
                isAdmin: offlineSession.isAdmin,
                isApproved: offlineSession.isApproved,
                isLoading: false,
                isOfflineMode: true,
                offlineSession,
              });
              return;
            }
          }
          
          lastUserIdRef.current = null;
          setAuthState({
            session: null,
            user: null,
            isAdmin: false,
            isApproved: false,
            isLoading: false,
            isOfflineMode: false,
            offlineSession: null,
          });
          return;
        }

        lastUserIdRef.current = session.user.id;

        const { isAdmin, isApproved } = await loadAccessFlags(session.user.id);
        
        // Save session for offline use
        if (isApproved) {
          await saveSessionForOffline(
            session.user.id,
            session.user.email || '',
            isAdmin,
            isApproved,
            session.user.user_metadata?.full_name
          );
          await updateOfflineSessionTimestamp();
        }
        
        if (!isMounted) return;

        setAuthState({
          session,
          user: session.user,
          isAdmin,
          isApproved,
          isLoading: false,
          isOfflineMode: false,
          offlineSession: null,
        });
      } catch (error) {
        console.error('Error initializing auth:', error);
        
        // If error is network-related and we're offline, try offline login
        if (!navigator.onLine) {
          const offlineSession = await tryOfflineLogin();
          if (offlineSession) {
            if (!isMounted) return;
            lastUserIdRef.current = offlineSession.userId;
            setAuthState({
              session: null,
              user: null,
              isAdmin: offlineSession.isAdmin,
              isApproved: offlineSession.isApproved,
              isLoading: false,
              isOfflineMode: true,
              offlineSession,
            });
            return;
          }
        }
        
        if (isMounted) {
          setAuthState((prev) => ({ ...prev, isLoading: false }));
        }
      }
    };

    initializeAuth();

    // Listen for online status changes to re-initialize auth
    const handleOnline = () => {
      if (authState.isOfflineMode) {
        console.log('📶 Back online, re-initializing auth...');
        initializeAuth();
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
    };
  }, [checkUserRole, checkUserApproval, saveSessionForOffline, tryOfflineLogin]);

  const signUp = async (email: string, password: string, fullName?: string, referralCode?: string) => {
    const redirectUrl = `${window.location.origin}/app`;

    const trimmedRef = referralCode?.trim().toUpperCase();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || '',
          ...(trimmedRef ? { referral_code_used: trimmedRef } : {}),
        },
      },
    });

    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { data, error };
  };

  const signOut = async () => {
    const uid = authState.user?.id || authState.offlineSession?.userId;
    if (uid) {
      try {
        await removeCachedData(`workspace_bundle_${uid}`);
      } catch {
        /* ignore */
      }
    }
    await clearOfflineSession();
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user: authState.user,
    session: authState.session,
    isAdmin: authState.isAdmin,
    isApproved: authState.isApproved,
    isAuthenticated: !!authState.user || authState.isOfflineMode,
    isLoading: authState.isLoading,
    isOfflineMode: authState.isOfflineMode,
    offlineSession: authState.offlineSession,
    signUp,
    signIn,
    signOut,
  };
}
